/**
 * Catalog config hook — reads the server-published rules bucket so UI
 * thresholds + input bounds stay in lock-step with backend validation.
 *
 * Endpoint: GET /v1/config/catalog
 *
 * Per ADR 0002 (three-bucket config), business rules live on the server
 * and are exposed for UI consumption. Pure display tunables remain in
 * `@features/catalog/config/ui.ts` (CATALOG_UI).
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@core/api/client';

export interface CatalogRules {
  subject_name_max_len: number;
  subject_duration_min_minutes: number;
  subject_duration_max_minutes: number;
  subject_max_students_min: number;
  subject_max_students_max: number;
  package_sessions_min: number;
  package_sessions_max: number;
  package_validity_days_min: number;
  package_validity_days_max: number;
  package_name_max_len: number;
  entitlement_expiry_urgent_days: number;
  entitlement_expiry_warning_days: number;
  entitlement_freeze_past_tolerance_days: number;
  flags: {
    auto_thaw_on_freeze_expiry: boolean;
    [k: string]: boolean;
  };
}

/** Conservative fallback used when the request is in-flight or fails. */
export const CATALOG_RULES_FALLBACK: CatalogRules = {
  subject_name_max_len: 200,
  subject_duration_min_minutes: 15,
  subject_duration_max_minutes: 360,
  subject_max_students_min: 1,
  subject_max_students_max: 100,
  package_sessions_min: 1,
  package_sessions_max: 500,
  package_validity_days_min: 1,
  package_validity_days_max: 1095,
  package_name_max_len: 200,
  entitlement_expiry_urgent_days: 7,
  entitlement_expiry_warning_days: 30,
  entitlement_freeze_past_tolerance_days: 1,
  flags: { auto_thaw_on_freeze_expiry: false },
};

export function useCatalogConfig() {
  const q = useQuery({
    queryKey: ['config', 'catalog'] as const,
    queryFn: () => api.get<CatalogRules>('/v1/config/catalog'),
    staleTime: 5 * 60 * 1000,
  });
  return { rules: q.data ?? CATALOG_RULES_FALLBACK, isLoading: q.isLoading };
}

